import { useCallback, useEffect, useRef, useState } from "react";

import {
  ACTIVITY_THROTTLE_MS,
  clearLastActivity,
  readInactivityConfig,
  readLastActivity,
  writeLastActivity,
} from "../utils/session";

const ACTIVITY_EVENTS = [
  "mousemove",
  "keydown",
  "click",
  "scroll",
  "touchstart",
];

/**
 * Tracks user activity and, after `timeoutMs - warningMs` idle, sets
 * `warningActive=true`. After a further `warningMs` the caller's
 * `onExpire()` fires. Returns { warningActive, remainingMs, stayLoggedIn,
 * expireNow, timeoutMs, warningMs }.
 */
export function useInactivityLogout({ enabled, onExpire }) {
  const { timeoutMs, warningMs } = readInactivityConfig();

  const [warningActive, setWarningActive] = useState(false);
  const [remainingMs, setRemainingMs] = useState(warningMs);

  const lastActivityRef = useRef(Date.now());
  const warningTimerRef = useRef(null);
  const expireTimerRef = useRef(null);
  const tickerRef = useRef(null);
  const lastPersistedRef = useRef(0);

  const clearTimers = () => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (expireTimerRef.current) clearTimeout(expireTimerRef.current);
    if (tickerRef.current) clearInterval(tickerRef.current);
    warningTimerRef.current = null;
    expireTimerRef.current = null;
    tickerRef.current = null;
  };

  const fireExpire = useCallback(() => {
    clearTimers();
    clearLastActivity();
    setWarningActive(false);
    if (typeof onExpire === "function") onExpire();
  }, [onExpire]);

  const scheduleWarning = useCallback(
    (fromTs) => {
      clearTimers();
      const untilWarning = Math.max(0, timeoutMs - warningMs - (Date.now() - fromTs));
      warningTimerRef.current = setTimeout(() => {
        setWarningActive(true);
        setRemainingMs(warningMs);
        // Countdown ticker for the modal.
        const start = Date.now();
        tickerRef.current = setInterval(() => {
          const left = warningMs - (Date.now() - start);
          setRemainingMs(Math.max(0, left));
        }, 250);
        expireTimerRef.current = setTimeout(fireExpire, warningMs);
      }, untilWarning);
    },
    [fireExpire, timeoutMs, warningMs]
  );

  const resetActivity = useCallback(
    (persistAlways = false) => {
      const now = Date.now();
      lastActivityRef.current = now;
      if (persistAlways || now - lastPersistedRef.current >= ACTIVITY_THROTTLE_MS) {
        writeLastActivity(now);
        lastPersistedRef.current = now;
      }
      if (warningActive) setWarningActive(false);
      scheduleWarning(now);
    },
    [scheduleWarning, warningActive]
  );

  const stayLoggedIn = useCallback(() => {
    resetActivity(true);
  }, [resetActivity]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      return undefined;
    }

    // Honour any activity timestamp persisted before a refresh — if the
    // deadline has already elapsed, expire immediately.
    const persisted = readLastActivity();
    const now = Date.now();
    const startFrom = persisted && now - persisted < timeoutMs ? persisted : now;
    lastActivityRef.current = startFrom;
    writeLastActivity(startFrom);
    lastPersistedRef.current = startFrom;

    if (now - startFrom >= timeoutMs) {
      fireExpire();
      return undefined;
    }

    const throttled = (evt) => {
      // `evt` may be undefined (e.g. from visibilitychange handler below).
      const t = Date.now();
      if (t - lastActivityRef.current < ACTIVITY_THROTTLE_MS && !evt?.forceReset) {
        // Skip persisting but keep the schedule accurate.
        lastActivityRef.current = t;
        return;
      }
      resetActivity();
    };

    ACTIVITY_EVENTS.forEach((e) =>
      window.addEventListener(e, throttled, { passive: true })
    );

    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      const stored = readLastActivity() ?? lastActivityRef.current;
      if (Date.now() - stored >= timeoutMs) fireExpire();
      else scheduleWarning(stored);
    };
    document.addEventListener("visibilitychange", onVisibility);

    scheduleWarning(startFrom);

    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, throttled));
      document.removeEventListener("visibilitychange", onVisibility);
      clearTimers();
    };
  }, [enabled, fireExpire, resetActivity, scheduleWarning, timeoutMs]);

  return {
    warningActive,
    remainingMs,
    stayLoggedIn,
    expireNow: fireExpire,
    timeoutMs,
    warningMs,
  };
}
